import { resolveOrderSubdomain, sendRequest } from '@/configs/requests'

function coverageIdentity(coverage, rootState) {
  const identity = {
    subdomain: resolveOrderSubdomain(rootState.microsite.meta),
    coverage_certificate: coverage?.coverage_certificate || coverage?.CertificateNumber,
    email_booking: coverage?.email_booking || coverage?.booking_number
  }

  const token = coverage?.lookupToken
  if (token) {
    identity.token = String(token)
  }

  return identity
}

function lookupTokenHeaders(coverage) {
  const token = coverage?.lookupToken
  return token ? { token: String(token) } : {}
}

export default {
  namespaced: true,

  state: {
    coverage: null,
    errorMessage: null,
    tokenError: null,
    tokenLookupInFlight: false,
    isLoading: false,
    increaseQuote: null,
    loading: {
      quote: false,
      payment: false
    },
    errors: {
      quote: null,
      payment: null,
      quoteStale: false
    }
  },

  getters: {
    increaseAllowed(state) {
      const flag = state.coverage?.CoverageIncreaseAllowed
      if (flag === true || flag === 1 || flag === '1') {
        return true
      }
      if (flag === false || flag === 0 || flag === '0') {
        return false
      }
      // PAM-02 not deployed: allow after successful lookup in non-production builds only.
      if (process.env.NODE_ENV !== 'production') {
        return Boolean(state.coverage)
      }
      return false
    },
    increaseBlockReason(state) {
      return state.coverage?.CoverageIncreaseBlockReason || 'Additional coverage is not available for this policy.'
    }
  },

  mutations: {
    setCoverage(state, coverage) {
      state.coverage = coverage
    },
    setErrorMessage(state, errorMessage) {
      state.errorMessage = errorMessage
    },
    setTokenError(state, tokenError) {
      state.tokenError = tokenError
    },
    setTokenLookupInFlight(state, tokenLookupInFlight) {
      state.tokenLookupInFlight = tokenLookupInFlight
    },
    setIsLoading(state, isLoading) {
      state.isLoading = isLoading
    },
    setIncreaseQuote(state, increaseQuote) {
      state.increaseQuote = increaseQuote
    },
    setLoading(state, loading) {
      state.loading = { ...state.loading, ...loading }
    },
    setErrors(state, errors) {
      state.errors = { ...state.errors, ...errors }
    },
    clearIncreaseFlow(state) {
      state.increaseQuote = null
      state.errors = { quote: null, payment: null, quoteStale: false }
    }
  },

  actions: {
    async getCoverage({ commit, rootGetters }, data = {}) {
      commit('setErrorMessage', null)
      commit('setTokenError', null)
      commit('setIsLoading', true)

      const subdomain = rootGetters['microsite/subdomain']
      const { token, ...fields } = data
      const body = { ...fields, subdomain }
      const headers = token ? { token: String(token) } : {}

      if (token) {
        commit('setTokenLookupInFlight', true)
      }

      let coverage

      try {
        coverage = await sendRequest('policy', 'POST', body, headers)
      } catch {
        coverage = { errors: '' }
      }

      commit('setIsLoading', false)
      if (token) {
        commit('setTokenLookupInFlight', false)
      }

      if (!coverage || coverage.errors) {
        const message = typeof coverage?.errors === 'string' && coverage.errors
          ? coverage.errors
          : 'Policy not found'
        if (token) {
          commit('setTokenError', message)
        } else {
          commit('setErrorMessage', message)
        }
      } else {
        const stored = { ...coverage, ...fields }
        if (token) {
          stored.lookupToken = String(token)
        }
        commit('setCoverage', stored)
      }
    },

    async getIncreaseQuote({ commit, state, rootState }, requestedCoverageAmount) {
      commit('setErrors', { quote: null, quoteStale: false })
      commit('setLoading', { quote: true })

      const body = {
        ...coverageIdentity(state.coverage, rootState),
        requested_coverage_amount: Number(requestedCoverageAmount)
      }

      let response

      try {
        response = await sendRequest(
          'policy/increase-quote',
          'POST',
          body,
          lookupTokenHeaders(state.coverage)
        )
      } catch {
        response = null
      }

      commit('setLoading', { quote: false })

      if (!response || response.ok === false || response.error) {
        commit('setErrors', { quote: response?.error || 'Unable to quote additional coverage.' })
        commit('setIncreaseQuote', null)
        return null
      }

      commit('setIncreaseQuote', response)
      return response
    },

    async getAptexxSession({ commit, state, rootState }, data) {
      commit('setErrors', { payment: null, quoteStale: false })
      const body = {
        subdomain: resolveOrderSubdomain(rootState.microsite.meta),
        ...data
      }

      return sendRequest(
        'order/aptexx-session',
        'POST',
        body,
        lookupTokenHeaders(state.coverage)
      )
    },

    async processIncreasePayment({ commit, state, rootState }, paymentData) {
      commit('setErrors', { payment: null, quoteStale: false })
      commit('setLoading', { payment: true })

      const finalAmountDue = paymentData.final_amount_due != null
        ? paymentData.final_amount_due
        : state.increaseQuote?.final_amount_due
      const due = Number(finalAmountDue)
      if (!Number.isFinite(due) || due <= 0) {
        commit('setLoading', { payment: false })
        commit('setErrors', {
          payment: 'Quoted amount due is missing. Please get a new quote and try again.',
          quoteStale: false
        })
        return null
      }

      const body = {
        ...coverageIdentity(state.coverage, rootState),
        requested_coverage_amount: paymentData.requested_coverage_amount,
        // PAM CoverageIncrease.Modification.FinalAmountDue — must match quoted total due
        final_amount_due: due,
        payment_method: 'CC',
        aptexx_method_id: paymentData.aptexx_method_id,
        aptexx_account_person_id: paymentData.aptexx_account_person_id,
        aptexx_integration_id: paymentData.aptexx_integration_id
      }

      let response

      try {
        response = await sendRequest(
          'policy/increase',
          'POST',
          body,
          lookupTokenHeaders(state.coverage)
        )
      } catch {
        response = null
      }

      commit('setLoading', { payment: false })

      if (!response || response.error || response.ok === false) {
        if (response?.error_code === 'QUOTE_STALE') {
          const staleMessage = response.error || 'Pricing updated. Please review the new amount before paying again.'
          commit('setIncreaseQuote', {
            ok: true,
            current_coverage_amount: response.current_coverage_amount,
            requested_coverage_amount: response.requested_coverage_amount,
            current_premium: response.current_premium,
            additional_premium_due: response.additional_premium_due,
            credit_card_processing_fee: response.credit_card_processing_fee,
            final_amount_due: response.final_amount_due
          })
          commit('setErrors', {
            payment: staleMessage,
            quote: staleMessage,
            quoteStale: true
          })
        } else {
          commit('setErrors', {
            payment: response?.error || 'Payment could not be completed.',
            quoteStale: false
          })
        }
        return null
      }

      commit('setCoverage', {
        ...state.coverage,
        ...response,
        // PAM CoverageGet after increase sometimes omits BookingTotal or sends 0;
        // keep the requested amount so the success step does not show $0.
        Maximum_Cancellation_Benefit: (response.Maximum_Cancellation_Benefit != null
          && Number(response.Maximum_Cancellation_Benefit) > 0)
          ? response.Maximum_Cancellation_Benefit
          : (paymentData.requested_coverage_amount
            || state.coverage?.Maximum_Cancellation_Benefit)
      })
      commit('clearIncreaseFlow')
      return response
    },

    clearIncreaseFlow({ commit }) {
      commit('clearIncreaseFlow')
    }
  }
}
